import {
  HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpHeaders, HttpInterceptorFn, HttpRequest,
} from '@angular/common/http';
import { Injector, inject, runInInjectionContext } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Observable, catchError, from, switchMap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/auth/services/auth.service';
import { AUTH_V2 } from 'src/app/auth/services/auth-v2.token';
import { SessionService } from 'src/app/core/services/sessionService/session.service';
import { AUTH_TOKEN_OVERRIDE, NO_AUTH_REDIRECT, RETRIED, SKIP_AUTH } from './auth-context';
import { isAuthPage, sessionEndAlertKey } from './auth-error.util';

// Platzhalter aus Bestandscode («Bearer null», «Bearer undefined», '') gelten als «kein Token»
const EMPTY_AUTHORIZATION = /^(Bearer\s*(null|undefined)?\s*)?$/i;
// Auth-Endpunkte nie über den Service Worker (0.9); dataGroups kennen keine Ausschlussmuster. Seit E2-Nacharbeit (T29)
// gibt es keine dataGroup «api-cache» mehr; der Header `ngsw-bypass` bleibt als Schutz für eine spätere dataGroup.
// Offline E2 Schritt 2: Baujournal ebenso – offline liest die App aus dem Schnappschuss, und ein gescheiterter Request
// muss als Netzfehler ankommen (Outbox mit gleichem Key), nicht als gecachte Antwort (Plan-Review W3)
// Offline E2 Schritt 3: Aufgaben ebenso – offline greift der Geräte-Fallback (Schnappschuss + Outbox-Overlay), und ein
// gescheiterter Schreib-Request muss als Netzfehler ankommen, nicht als gecachte Antwort (nur /tasks, nicht /tasksfoo)
// Offline E2 Schritt 4: GET /projects/:id/documents/:key und die Liste /projects/:id/documents liefern signierte S3-Links:
// nie in den SW-Cache (nicht pro User, nach 2 s käme ein abgelaufener Link). Eine gecachte Liste verdeckte zudem einen
// Funkloch, der Datenraum fiele nicht auf die Geräteliste zurück (final review I2). Unterpfade wie /pins nicht
// E2-Gate FE-G4: die Gast-Dokumentliste (projects/shared/:id/documents, …/documents/entities) ebenso (signierte Links)
const SW_BYPASS_PATH =
  /^\/(users\/(login|register|forgot-password|reset-password|confirm-email|change-password|resend-confirm-email)|auth\/|construction-journals\/|tasks(\/|\?|$)|projects\/[^/?]+\/documents(\/[^/?]+)?(\?|$)|projects\/shared\/[^/?]+\/documents(\/entities)?(\?|$))/;
// Anfragen, die das Refresh-Cookie setzen oder lesen (OAuth Phase 1): Cookie mitsenden und CSRF-Header (S10)
const CREDENTIALS_PATH = /^\/(auth\/|users\/(login|register)(\?|$))/;
// Token-Endpunkte (Refresh, Migration, Logout): ein 401 dort löst nie selbst einen Refresh aus
const AUTH_ENDPOINT = /^\/auth\//;

interface SessionDeps {
  authService: AuthService;
  injector: Injector;
  router: Router;
  toastr: ToastrService;
  translate: TranslateService;
}

export function isApiRequest(url: string): boolean {
  return url.startsWith(environment.apiUrl);
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // Externe Ziele (S3, i18n-Assets) unverändert lassen. inject() erst danach: der
  // TranslateHttpLoader lädt über HttpClient, ein früher inject(TranslateService) wäre ein DI-Zyklus.
  if (!isApiRequest(req.url)) return next(req);
  // E2-Folge FE-5: a refresh already running in this tab (reconnect after a cold offline start) → wait for its token
  // instead of sending without one (401, second round trip). Not for guest calls, explicit tokens or the auth endpoints
  // themselves (the refresh request would wait for itself).
  const pending = waitsForRefresh(req) ? inject(AuthService).pendingRefresh() : null;
  if (pending === null) return intercept(req, next);
  const injector = inject(Injector);
  return from(pending).pipe(switchMap(() => runInInjectionContext(injector, () => intercept(req, next))));
};

function waitsForRefresh(req: HttpRequest<unknown>): boolean {
  const path = req.url.slice(environment.apiUrl.length);
  return (
    !AUTH_ENDPOINT.test(path) &&
    !req.context.get(SKIP_AUTH) &&
    req.context.get(AUTH_TOKEN_OVERRIDE) === null &&
    // a placeholder («Bearer null», '') is dropped below and replaced by the current token: it waits too
    EMPTY_AUTHORIZATION.test(req.headers.get('Authorization') ?? '')
  );
}

/** only API requests (authInterceptor checks isApiRequest first) */
const intercept = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
  const deps: SessionDeps = {
    authService: inject(AuthService),
    injector: inject(Injector),
    router: inject(Router),
    toastr: inject(ToastrService),
    translate: inject(TranslateService),
  };

  let headers: HttpHeaders = req.headers.set('ngrok-skip-browser-warning', '555');
  const path = req.url.slice(environment.apiUrl.length);
  if (SW_BYPASS_PATH.test(path)) {
    headers = headers.set('ngsw-bypass', 'true');
  }
  // Nur mit authV2: im Legacy-Modus bleiben Login und Registrierung ohne Credentials (cross-origin von
  // localhost gegen dev.bavox.ch gäbe es sonst ohne Access-Control-Allow-Credentials keine Antwort)
  const withCredentials = inject(AUTH_V2) && CREDENTIALS_PATH.test(path);
  if (withCredentials) {
    headers = headers.set('X-Bavox-Client', 'app');
  }
  const existing = headers.get('Authorization');
  if (existing !== null && EMPTY_AUTHORIZATION.test(existing)) {
    headers = headers.delete('Authorization');
  }

  let sentToken: string | null = null;
  if (req.context.get(SKIP_AUTH)) {
    // Gast-Aufruf: auch ein explizit gesetztes Token nie mitsenden
    headers = headers.delete('Authorization');
  } else {
    const explicit = headers.get('Authorization');
    if (explicit !== null) {
      sentToken = explicit.replace(/^Bearer\s+/i, '');
    } else {
      const token = req.context.get(AUTH_TOKEN_OVERRIDE) ?? deps.authService.getToken();
      if (token) {
        headers = headers.set('Authorization', `Bearer ${token}`);
        sentToken = token;
      }
    }
  }

  return next(req.clone(withCredentials ? { headers, withCredentials: true } : { headers })).pipe(
    catchError((error: unknown) => {
      // v2 (OAuth Phase 1): abgelaufenes oder fehlendes Access Token → ein Refresh, dann eine Wiederholung.
      // Nicht bei Gast-Aufrufen, beim Offline-Sync (regelt das selbst), Wiederholungen und Token-Endpunkten.
      if (
        error instanceof HttpErrorResponse &&
        error.status === 401 &&
        deps.authService.mode === 'v2' &&
        !req.context.get(SKIP_AUTH) &&
        !req.context.get(NO_AUTH_REDIRECT) &&
        !req.context.get(RETRIED) &&
        !AUTH_ENDPOINT.test(path)
      ) {
        const retry = (accessToken: string) => {
          const retried = req.clone({
            headers: headers.set('Authorization', `Bearer ${accessToken}`),
            context: req.context.set(RETRIED, true),
          });
          return next(retried).pipe(
            catchError((retryError: unknown) => {
              if (retryError instanceof HttpErrorResponse && retryError.status === 401) {
                endInvalidSession(accessToken, retryError, deps);
              }
              return throwError(() => retryError);
            }),
          );
        };
        // Mit einem älteren Token gesendet, das inzwischen (paralleler Refresh, anderer Tab) ersetzt ist: mit dem
        // aktuellen wiederholen statt erneut zu rotieren
        const current = deps.authService.getToken();
        if (current !== null && current !== sentToken) return retry(current);
        // Vor dem Refresh bestimmen: ein gescheiterter Refresh löscht die Sitzung (Rücksprung-URL gehört diesem User)
        const userId = deps.authService.getUserId();
        return from(deps.authService.refresh({ retryNetwork: true })).pipe(
          switchMap((outcome) => {
            if (outcome.kind === 'ok') return retry(outcome.accessToken);
            // Ohne Netz (nach 2/5/15 s) oder abgelehnt (403, z.B. CSRF): Fehler an den Aufrufer, Sitzung bleibt
            if (outcome.kind === 'auth' || outcome.kind === 'unavailable') {
              deps.injector.get(SessionService).endRejectedSession({
                sentToken, userId, alertKey: sessionEndAlertKey(error, outcome), once: outcome,
              });
            }
            return throwError(() => error);
          }),
        );
      }
      if (
        error instanceof HttpErrorResponse &&
        error.status === 401 &&
        sentToken !== null &&
        !req.context.get(NO_AUTH_REDIRECT)
      ) {
        endInvalidSession(sentToken, error, deps);
      }
      return throwError(() => error);
    }),
  );
};

function endInvalidSession(sentToken: string, error: HttpErrorResponse, deps: SessionDeps): void {
  const { authService, injector, router, toastr, translate } = deps;
  // Inzwischen neu angemeldet, abgemeldet oder von einem parallelen 401 bereits behandelt
  if (authService.getToken() !== sentToken) return;
  // Ziel einer laufenden Navigation bevorzugen (401 beim Laden der neuen Seite), sonst aktuelle Seite
  const url = router.currentNavigation()?.finalUrl?.toString() ?? router.url;
  // Kein Redirect von den Auth-Seiten auf sich selbst (Login, Passwort, Registrierung)
  const onAuthPage = isAuthPage(url);
  // Vor dem Aufräumen setzen: AuthService merkt sich dabei den User des noch gespeicherten Tokens,
  // damit nach einem User-Wechsel im selben Tab niemand auf der URL des vorigen Users landet.
  if (!onAuthPage) authService.redirectUrl = url;
  // Gleiches Aufräumen wie beim Logout (Socket, Bild-Kontext, ab F7 Reste des SW-Daten-Caches), aber ohne
  // dessen Navigation. Offline-Warteschlange (IndexedDB), Entwürfe und Filter bleiben erhalten.
  // Erst hier auflösen: SessionService hängt über TasksService selbst an HttpClient.
  injector.get(SessionService).endSession({ navigate: false });
  if (onAuthPage) return;
  const key = sessionEndAlertKey(error);
  toastr.info(translate.instant(key), '', { positionClass: 'toast-top-center' });
  void router.navigate(['/auth/login']);
}
